import USStates from '@deltek/specification-client/core/Constants/usStates'
import type { AssociationModifierList } from '@deltek/specification-client/core/Models/associationModifierList'
import { availableProjectParamters } from 'shared/utils/projectParameterUtils'
import {
    emptyParameterSelected,
    ParameterKey,
    ParameterOptionsMap,
} from '../types/bpManagement.types'

export const labelToParameterKey: Record<string, ParameterKey> = {
    Owner: 'owner',
    'Building Type': 'buildingType',
    'Construction Type': 'constructionType',
    'Contract Type': 'contractType',
    Sustainability: 'sustainability',
    'Geographic Region': 'geographicRegion',
    'Seismic Zone': 'seismicZone',
    'Wind Zone': 'windZone',
}

interface FirmAssociationModifier {
    id?: string
    name?: string
}

interface FirmProjectGroup {
    name?: string
}

export interface FirmModifierLookupEntry {
    modifierListId: string
    modifiers: Record<string, string>
}
export type FirmModifierLookup = Partial<
    Record<ParameterKey, FirmModifierLookupEntry>
>

export interface FirmDataShape {
    firm?: {
        projectGroups?: FirmProjectGroup[]
        associationModifierLists?: Partial<AssociationModifierList>[]
    }
}

const buildEmptyOptionsMap = (): ParameterOptionsMap => ({
    ...emptyParameterSelected,
    provinceState: Object.values(USStates),
})

/**
 * Walk firmData once and produce both shapes the BP feature needs:
 *  - `options`: ParameterKey → string[] (names) for the panel dropdowns
 *  - `lookup`: ParameterKey → modifier-list id + name→id map for mutation input
 */
export const buildFirmBPParameters = (
    firmData: FirmDataShape | null | undefined
): { options: ParameterOptionsMap; lookup: FirmModifierLookup } => {
    const options = buildEmptyOptionsMap()
    const lookup: FirmModifierLookup = {}

    const groups = firmData?.firm?.projectGroups
    if (groups) {
        options.projectGroup = groups
            .map((g) => g.name)
            .filter((n): n is string => Boolean(n))
    }

    const lists = firmData?.firm?.associationModifierLists
    if (lists) {
        lists.forEach((modifierList) => {
            const paramConfig = availableProjectParamters.find(
                (p) => p.name === modifierList.name
            )
            if (!paramConfig) return
            const paramKey = labelToParameterKey[paramConfig.label]
            if (!paramKey) return

            const modifiers: Record<string, string> = {}
            const names: string[] = []
            ;(modifierList.associationModifiers || []).forEach(
                (mod: FirmAssociationModifier) => {
                    if (!mod.name) return
                    names.push(mod.name)
                    if (mod.id) modifiers[mod.name] = mod.id
                }
            )

            options[paramKey] = names
            lookup[paramKey] = {
                modifierListId: modifierList.id || '',
                modifiers,
            }
        })
    }

    return { options, lookup }
}
